// Three & GSAP
import * as THREE from 'three';
import GSAP from 'gsap';
import each from 'lodash/each';
import normalizeWheel from 'normalize-wheel';

import GlobalHandler from '../../../classes/GlobalHandler';
import { Detection } from '../../../classes/Detection';

// Classes
import Component from '@averyano/core/src/classes/Component';
import { SHADERS, uvCrop, buildImageArray } from './GalleryItem';

// Utils
import { lerp } from '../../../utils/utils';
import { clamp } from 'three/src/math/MathUtils';

// px per texture-array layer (square, aspect-fit + crop). Smaller on mobile to
// cut GPU memory: 76 unique × L² × 4 bytes → ~75MB @512 vs ~300MB @1024.
const LAYER_SIZE = Detection.isMobile ? 512 : 1024;

export default class Gallery extends Component {
	constructor({ scene, sizes }) {
		super({
			element: '.cover',
			elements: {
				wrapper: '.cover__wrapper',
				images: '.cover__image',
			},
		});

		this.scene = scene;
		this.sizes = sizes;
		this.extraSpeed = 200;
		this.textureLoader = new THREE.TextureLoader();
		this.isBoundReady = false;

		this.isPlayed = false;

		this.items = [];

		// Animation related
		this.speed = {
			current: 0,
			target: 0,
			lerp: 0.1,
		};

		this.velocity = 0;
		this.direction = 1;

		this.maxHeight = 0;

		this.isMobile = this.sizes.width < 768;

		// Wrap-seam padding. Must equal the CSS `.cover__item` padding for the
		// current width so the last→first gap matches the normal inter-image gap.
		// Recomputed in getBounds() (initial build + resize) via getItemPadding().
		this.padding = this.getItemPadding();

		this.active = false;
		this.previous = null;
		this.isAnimating = false;
		this.isShowing = false;
		this.isRescaling = false;
		this.isPlayed = false;
		this.isKeyDown = false;
		this.isAutoplay = true;
		this.time = {
			start: 0,
			current: 0,
			end: 0,
		};

		this.isPausedGlobally = false;
		this.speedMulti = 1;
		this.speedExtraMotion = 1;

		this.metricsLength = 0;
		this.metricsTargetBreakdown = -1;
		this.metricsBreakdowns = [];
		this.currentBreakdownIndex = -1;
		this.metricsBreakdownsNamings = ['25%', '50%', '75%', '100%'];

		// Starts from 0, its the image that activates breakpoint
		this.currentHeight = 0;
		this.projectBreakpoints = [
			{
				name: 'SBID 21/22<br>Overall Winner',
				be: 'https://www.behance.net/gallery/152412439/GREEN-APARTMENT-WINNER-GET-ME-2THE-TOP',
				indexStart: 0,
				indexEnd: 8,
				height: {
					start: 0,
					end: 0,
				},
			},
			{
				name: 'SBID 20/21<br>Overall Winner',
				be: 'https://www.behance.net/gallery/123851279/OVERALL-WINNER-SBID-2021-GET-ME-2THE-TOP',
				indexStart: 8,
				indexEnd: 19,
				height: {
					start: 0,
					end: 0,
				},
			},
			{
				name: 'Apartment in the Passage<br>(99,4 m²)',
				be: 'https://www.behance.net/gallery/123854347/APARTMENT-INTHE-PASSAGE',
				indexStart: 19,
				indexEnd: 38,
				height: {
					start: 0,
					end: 0,
				},
			},
			{
				name: 'Realization<br>Kyiv, str. Volodymyrska',
				be: 'https://www.behance.net/gallery/182159243/VISUALIZATION-VS-REALIZATION',
				indexStart: 38,
				indexEnd: 48,
				height: {
					start: 0,
					end: 0,
				},
			},
			{
				name: 'Berkeley Travel Office<br>(21,4 m²)',
				be: 'https://www.behance.net/gallery/152375641/BERKELEY-TRAVELOFFICE',
				indexStart: 48,
				indexEnd: 53,
				height: {
					start: 0,
					end: 0,
				},
			},
			{
				name: 'Children Room Interior<br>(22,3 m²)',
				be: 'https://www.behance.net/gallery/151258105/CHILDRENS-ROOM',
				indexStart: 53,
				indexEnd: 59,
				height: {
					start: 0,
					end: 0,
				},
			},
			{
				name: '',
				be: 'https://www.behance.net/gallery/151258105/CHILDRENS-ROOM',
				indexStart: 59,
				indexEnd: 74,
				height: {
					start: 0,
					end: 0,
				},
			},
		];
		this.isForwarding = false;
		this.projectBreakpointIndex = 0;
		this.projectTargetBreakpoint = 0;

		// Intro reveal: hold the wall still + suppress autoplay/analytics until the
		// IntroTimeline finishes the camera fly-in + image stagger.
		this.introActive = true;
		this.tf = [];

		this.addEventListeners();
	}

	// Upload per-image authored transforms (this.tf) into the instanced attributes.
	// Called every frame ONLY while the intro timeline is active; at rest the
	// attributes keep their identity values and nothing is uploaded.
	syncTf() {
		if (!this.imageMesh || !this.tf.length) return;
		this.tf.forEach((t, a) => {
			this.attr.aOffset.array[a * 2] = t.x;
			this.attr.aOffset.array[a * 2 + 1] = t.y;
			this.attr.aRot.array[a] = t.rot;
			this.attr.aScale.array[a] = t.scale;
			this.attr.aAlpha.array[a] = t.alpha;
		});
		this.attr.aOffset.needsUpdate = true;
		this.attr.aRot.needsUpdate = true;
		this.attr.aScale.needsUpdate = true;
		this.attr.aAlpha.needsUpdate = true;
	}

	// Wait until every DOM `.cover__image` has decoded (natural size applied) and
	// one frame has painted, so getBounds() reads final laid-out rects. Measuring
	// too early froze a stale (taller) height into the mesh, shifting everything
	// below it down → a white band mid-scroll that only a later resize re-measure
	// cleared. decode() resolves fast for already-loaded imgs; errors are ignored.
	settleLayout() {
		const imgs = Array.from(this.elements.images || []);
		const decoded = Promise.all(
			imgs.map((img) =>
				img && img.decode ? img.decode().catch(() => {}) : Promise.resolve()
			)
		);
		// Never block the intro indefinitely if a decode hangs.
		const timeout = new Promise((r) => setTimeout(r, 2000));
		return Promise.race([decoded, timeout]).then(
			() => new Promise((r) => requestAnimationFrame(() => r()))
		);
	}

	createItems() {
		return new Promise((res) => {
			this.settleLayout().then(() => {
				this.getBounds().then((imageBounds) => {
					const srcs = imageBounds.map((obj) => obj.src);

					buildImageArray(srcs, LAYER_SIZE).then(({ texture, info }) => {
						this.array = texture;
						this.info = info;

						this.buildMesh(imageBounds.length);
						this.setBounds(); // seed per-instance attrs + uPeriod

						// `items` length stays === image count so Experience.updateImages
						// keeps distinguishing first build from resize re-seed.
						this.items = imageBounds.map((_, i) => ({ index: i }));

						// per-image authored transform for the intro (0/1 = at rest)
						this.tf = imageBounds.map(() => ({
							x: 0,
							y: 0,
							rot: 0,
							scale: 1,
							alpha: 1,
						}));

						this.isReady = true;
						this.isBoundReady = true;
						GSAP.set(this.elements.wrapper, { autoAlpha: 0 }); // hides the DOM element
						res();
						this.onResize();
					});
				});
			});
		});
	}

	// Build the single instanced mesh: one geometry, one DataArrayTexture, one
	// RawShaderMaterial → 1 draw call for every image.
	buildMesh(count) {
		this.count = count;
		const base = new THREE.PlaneGeometry(1, 1);

		const geo = new THREE.InstancedBufferGeometry();
		geo.index = base.index;
		geo.setAttribute('position', base.attributes.position);
		geo.setAttribute('uv', base.attributes.uv);
		geo.instanceCount = count;
		base.dispose();

		const f2 = () =>
			new THREE.InstancedBufferAttribute(new Float32Array(count * 2), 2);
		const f1 = () =>
			new THREE.InstancedBufferAttribute(new Float32Array(count), 1);
		this.attr = {
			aBase: f2(),
			aSize: f2(),
			aUvRepeat: f2(),
			aUvOffset: f2(),
			aLayer: f1(),
			// intro transform (uploaded via syncTf only while the intro plays)
			aOffset: f2(),
			aRot: f1(),
			aScale: f1(),
			aAlpha: f1(),
		};
		this.attr.aScale.array.fill(1); // identity scale at rest
		this.attr.aAlpha.array.fill(1); // fully opaque at rest
		Object.keys(this.attr).forEach((k) => geo.setAttribute(k, this.attr[k]));

		this.material = new THREE.RawShaderMaterial({
			glslVersion: THREE.GLSL3,
			uniforms: {
				uArray: { value: this.array },
				uScroll: { value: 0 },
				uPeriod: { value: 1 },
			},
			vertexShader: SHADERS.IMAGE_VERT,
			fragmentShader: SHADERS.IMAGE_FRAG,
			transparent: true,
			depthTest: false,
			depthWrite: false,
		});

		this.imageGeo = geo;
		this.imageMesh = new THREE.Mesh(geo, this.material);
		this.imageMesh.frustumCulled = false;
		this.scene.add(this.imageMesh);
	}

	// Read every figure's DOM rect once (first frame + resize), seed per-instance
	// base/size/crop, and set the tiling period. No per-mesh objects to move.
	setBounds() {
		if (!this.imageMesh || !this.imageBounds) return;
		const vw = this.sizes.width;
		const vh = this.sizes.height;

		this.imageBounds.forEach((obj, a) => {
			const b = obj.bounds;
			if (!b.width || !b.height) return;

			const cx = (b.left + b.right) / 2;
			const cy = (b.top + b.bottom) / 2;
			const baseX = cx - vw / 2; // world center, origin = screen center, +Y up
			const baseY = -cy + vh / 2;

			const meta = this.info.get(obj.src) || {
				layer: 0,
				fit: { repeat: [1, 1], offset: [0, 0] },
				aspect: 1,
			};
			const crop = uvCrop('cover', b.width, b.height, meta.aspect);

			// compose layer-fit with the cover crop so the letterbox is never sampled
			const fr = meta.fit.repeat;
			const fo = meta.fit.offset;
			const repeat = [crop.repeat[0] * fr[0], crop.repeat[1] * fr[1]];
			const offset = [
				fo[0] + crop.offset[0] * fr[0],
				fo[1] + crop.offset[1] * fr[1],
			];

			this.attr.aBase.array[a * 2] = baseX;
			this.attr.aBase.array[a * 2 + 1] = baseY;
			this.attr.aSize.array[a * 2] = crop.width;
			this.attr.aSize.array[a * 2 + 1] = crop.height;
			this.attr.aUvRepeat.array[a * 2] = repeat[0];
			this.attr.aUvRepeat.array[a * 2 + 1] = repeat[1];
			this.attr.aUvOffset.array[a * 2] = offset[0];
			this.attr.aUvOffset.array[a * 2 + 1] = offset[1];
			this.attr.aLayer.array[a] = meta.layer;
		});

		Object.values(this.attr).forEach((at) => (at.needsUpdate = true));
		this.material.uniforms.uPeriod.value = this.maxHeight;
	}

	// Resize entry point: re-read rects then re-seed (no per-mesh writes).
	updateItems(imageBounds) {
		if (!imageBounds) {
			console.warn('No image bounds');
			return;
		}
		this.setBounds();
	}

	checkMaxHeight(bounds) {
		// Rebase to the gallery's own top so the tiling period is the CONTENT
		// height, not the viewport-relative bottom. Without this, any top offset
		// present at measure time (mobile dynamic viewport, preloader transform,
		// scroll) inflates the period → an empty white band at the wrap seam.
		// `.cover` sits at y≈0 on desktop, so this is a no-op there.
		const top0 = this.wrapperBounds ? this.wrapperBounds.top : 0;
		this.maxHeight = Math.max(
			this.maxHeight,
			bounds.height + bounds.top - top0
		);
	}

	// CSS `.cover__item` padding for the current viewport, mirroring the SCSS
	// breakpoints in styles/utils/responsive.scss (phone 769, tablet 1024,
	// laptop 1440). Kept in sync so the wrap seam matches the normal gap.
	getItemPadding() {
		const w = this.sizes.width;
		if (w < 769) return 6; // <phone
		if (w < 1024) return 24; // <tablet
		if (w < 1440) return this.sizes.height * 0.05; // <laptop → 5vh
		return 120; // desktop
	}

	getBounds() {
		this.wrapperBounds = this.element.getBoundingClientRect();

		return new Promise((resolve) => {
			// Create Images Array
			if (!this.imagesArray) {
				this.imagesArray = new Array(this.elements.images.length);
				each(this.elements.images, (image, i) => {
					this.imagesArray[i] = {
						bounds: image.getBoundingClientRect(),
						src: GlobalHandler.isWebpSupported
							? image.dataset.preWebp
							: image.dataset.pre,
					};
					this.checkMaxHeight(this.imagesArray[i].bounds);
				});
				this.padding = this.getItemPadding();
				this.maxHeight += this.padding; // padding for the last image
			}

			// Update Existing Images Array
			if (this.imagesArray) {
				this.maxHeight = 0;
				this.imagesArray.map((image, i) => {
					image.bounds = this.elements.images[i].getBoundingClientRect();
					this.checkMaxHeight(this.imagesArray[i].bounds);
					this.projectBreakpoints.map((project) => {
						if (i === project.indexStart) {
							project.height.start = this.maxHeight;
						}
						if (i === project.indexEnd) {
							project.height.end = this.maxHeight;
						}
					});
				});
				this.padding = this.getItemPadding();
				this.maxHeight += this.padding; // padding for the last image
			}

			this.metricsBreakdowns = [
				this.maxHeight * 0.25,
				this.maxHeight * 0.5,
				this.maxHeight * 0.75,
				this.maxHeight,
			];
			this.metricsTargetBreakdown = this.metricsBreakdowns[0];
			this.currentBreakdownIndex = 0;
			// Return bounds
			this.imageBounds = this.imagesArray;
			this.$emit('projectChange', this.projectBreakpoints[0]);
			resolve(this.imagesArray);
		});
	}

	/* INITIAL ANIMATION */
	show() {
		this.isShowing = true;
		this.direction = 1;

		// @TODO create a different animation
		// if (this.items.length > 0) {
		// 	GSAP.killTweensOf(this.speed);
		// 	GSAP.fromTo(
		// 		this.speed,
		// 		{
		// 			target: 1000,
		// 		},
		// 		{
		// 			target: 2,
		// 			duration: 3,
		// 			ease: 'power4.inOut',
		// 			// ease: 'in.expo',
		// 			onComplete: () => {
		// 				this.isShowing = false;
		// 				this.isPlayed = true;
		// 			},
		// 		}
		// 	);
		// }
	}

	hide() {
		// this overlay mesh opacity 1
	}

	/* RAF */
	update() {
		if (this.isRescaling || !this.isBoundReady) return;

		if (this.isAutoplay) {
			this.speed.current =
				1 * this.speedMulti * this.speedExtraMotion * this.direction;
		} else {
			this.speed.current = lerp(
				this.speed.current,
				this.speed.target,
				this.speed.lerp
			);
		}

		if (!this.isShowing && this.speedExtraMotion === 1)
			this.speed.current = clamp(this.speed.current, -120, 120);

		this.speed.target = this.velocity * this.direction;
		this.currentHeight += this.speed.current;

		// Whole-batch motion + infinite wrap now happen in the vertex shader from
		// this single uniform (was a per-mesh position write + teleport loop).
		if (this.material)
			this.material.uniforms.uScroll.value = this.currentHeight;

		// During the intro the wall already auto-scrolls (above), but user input is
		// locked and analytics/projectChange are paused until the reveal finishes.
		if (this.introActive) return;

		requestIdleCallback(() => {
			if (this.currentHeight >= this.maxHeight) {
				console.log('Reached Max Height!');
				this.currentHeight = 0;
				this.projectTargetBreakpoint = 0;
				this.$emit(
					'projectChange',
					this.projectBreakpoints[this.projectTargetBreakpoint]
				);
			}
			if (this.currentHeight < 0) {
				this.currentHeight = this.maxHeight;
				this.projectTargetBreakpoint = this.projectBreakpoints.length - 1;
				this.$emit(
					'projectChange',
					this.projectBreakpoints[this.projectTargetBreakpoint]
				);
			}
			if (
				this.currentHeight >
					this.projectBreakpoints[this.projectTargetBreakpoint].height.end &&
				this.direction === 1
			) {
				if (this.projectBreakpoints[this.projectTargetBreakpoint + 1]) {
					this.projectTargetBreakpoint++;
				}
				this.$emit(
					'projectChange',
					this.projectBreakpoints[this.projectTargetBreakpoint]
				);
			}
			if (
				this.currentHeight <=
					this.projectBreakpoints[this.projectTargetBreakpoint].height.start &&
				this.direction === -1
			) {
				if (this.projectBreakpoints[this.projectTargetBreakpoint - 1]) {
					this.projectTargetBreakpoint--;
				}
				this.$emit(
					'projectChange',
					this.projectBreakpoints[this.projectTargetBreakpoint]
				);
			}
		});

		if (this.currentBreakdownIndex === -1) return;

		requestIdleCallback(() => {
			this.metricsLength += Math.abs(this.speed.current);

			if (this.metricsLength >= this.metricsTargetBreakdown) {
				window.globalAnalyticsInstance.trackEvent(
					'Home Gallery',
					{
						action: 'Reached',
						label: `${
							this.metricsBreakdownsNamings[this.currentBreakdownIndex]
						}`,
					},
					1
				);

				// Move to next breakdown target
				this.currentBreakdownIndex++;

				if (this.currentBreakdownIndex < this.metricsBreakdowns.length) {
					this.metricsTargetBreakdown =
						this.metricsBreakdowns[this.currentBreakdownIndex];
					console.log('New Target', this.metricsTargetBreakdown);
				} else {
					// Handle the case when you have reached the end of the breakdowns array.
					// For example, you might want to stop further checks or reset the index.
					console.log('All targets reached!');
					this.currentBreakdownIndex = -1; // This line resets the index if needed
				}
			}
		});
	}

	onResize() {
		this.isRescaling = true;
		this.hide();
		// Gets bounds, updates meshes positions and scaling
		if (this.imageBounds) {
			this.getBounds().then(() => {
				this.updateItems(this.imageBounds);

				requestIdleCallback(() => {
					this.isRescaling = false;
				});

				if (this.isPlayed) {
					// this.show();
					this.playRaf();
				} // to show it one time only
				// this.show();
				this.isPlayed = true;
				console.log('show WebGL');
			});
		}
	}

	/* EVENTS */
	addEventListeners() {
		/* added in app.js */
	}

	onTouchDown({ x, y }) {
		if (this.isForwarding || this.introActive) return;
		// this.speed.target = this.speed.current - yDistance;
		this.speedCurrent = this.speed.current;
		this.isAutoplay = false;
		if (this.timer) clearTimeout(this.timer);
	}

	onTouchMove({ y }) {
		if (!this.isPlayed || this.isForwarding || this.introActive) return;
		const yDistance = y.start - y.end;
		this.direction = yDistance < 0 ? -1 : 1;
		// this.speed.target = this.speed.current - yDistance;
		// this.speed.target = clamp(this.speedCurrent + yDistance, -40, 40);
		this.speed.target = this.speedCurrent + yDistance * 3;
	}

	onTouchUp({ x, y }) {
		if (!this.isPlayed || this.isForwarding || this.introActive) return;
		this.speed.target = 2 * this.velocity * this.direction;

		this.pauseRaf();
	}

	onWheel(direction, pixelY) {
		if (!this.isPlayed || this.isForwarding || this.introActive) return;
		this.direction = direction;
		this.speed.target += pixelY * 3;

		this.pauseRaf();
	}

	onKeyDown(e) {
		if (this.isForwarding || this.introActive) return;
		if (!this.isKeyDown) {
			this.time.start = Date.now();
			this.isKeyDown = true;
		}

		this.time.current = Date.now();
		let diff = 100 * ((this.time.current - this.time.start) / 1000);

		this.pauseRaf();

		if (e.code === 'ArrowUp' || e.keyCode === 38) {
			e.preventDefault();
			this.direction = -1;
			this.speed.target -= diff;
		}
		if (e.code === 'ArrowDown' || e.keyCode === 40) {
			e.preventDefault();
			this.direction = 1;
			this.speed.target += diff;
		}
	}

	onKeyUp(e) {
		this.time = {
			start: null,
			current: null,
			end: null,
		};

		this.isKeyDown = false;

		// this.speed.target = 0;
	}

	destroy() {
		if (this.imageMesh) {
			this.scene.remove(this.imageMesh);
			this.imageGeo.dispose();
			this.material.dispose();
		}
		if (this.array) this.array.dispose();
		this.items = [];
		this.isReady = false;
	}

	pauseRaf() {
		this.velocity = 0;
		this.isAutoplay = false;

		if (this.timer) clearTimeout(this.timer);
		if (this.isPausedGlobally) return;
		this.timer = setTimeout(() => {
			this.playRaf();
		}, 5000);
	}

	playRaf() {
		this.isAutoplay = true;
		// this.velocity = 1;
	}
}
